import React from "react";
import { Stack } from "@mui/material";
import { NormalText } from "components/styled/text.styled";
import styled from "styled-components";
import { formatCurrency } from "helper/general";
import useRedux from "redux/useRedux";
import CounterButton from "components/Counter";
import { IChangeQuantity, changeQuantity } from "redux/cart/cart.reducers";
import ConfirmationModal from "components/Confirmation";
import { BillConfirmationIcon } from "assets/icons";
import NoPhoto from "assets/images/no-product-photo.svg";

interface IProductItem {
  productId: number;
  image: string;
  name: string;
  product_price: number; // harga asli
  price_after_discount?: number; // harga setelah diskon
  onButtonClick: any;
  onBoxClick: any;
  customize: boolean;
  stock: number;
  is_stock: boolean;
  discount?: number;
  discount_type?: string;
}

const ProductItem = ({
  image,
  name,
  product_price,
  price_after_discount,
  customize,
  onButtonClick,
  onBoxClick,
  productId,
  stock,
  is_stock,
  discount = 0,
  discount_type,
}: IProductItem) => {
  const {
    thunkDispatch,
    storeState: { Cart },
  } = useRedux();
  const [remove, setRemove] = React.useState(false);
  const [removeId, setRemoveId] = React.useState("");
  const [imageError, setImageError] = React.useState(false);
  const isOutOfStock = is_stock && stock === 0;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleChangeQuantity = (param: IChangeQuantity) => {
    thunkDispatch(changeQuantity(param))
      .unwrap()
      .then(() => setRemove(false));
  };

  return (
    <ProductBox onClick={isOutOfStock ? undefined : onBoxClick}>
      <ConfirmationModal
        open={remove}
        icon={BillConfirmationIcon}
        titleStyle={{ margin: "15px 0" }}
        title={"Apakah anda yakin akan membatalkan item?"}
        handleClose={() => setRemove(false)}
        handleConfirm={() =>
          handleChangeQuantity({
            cart_id: removeId ?? "",
            type: "remove",
          })
        }
      />

      {/* Label Diskon */}
      {discount !== 0 && (
        <DiscountFlag>
          {discount_type === "percent"
            ? `${discount}% off`
            : `${formatCurrency(discount)} IDR off`}
        </DiscountFlag>
      )}

      {/* Gambar Produk */}
      {imageError ? (
        <ProductImage disabled={isOutOfStock} src={NoPhoto} alt={"sample"} />
      ) : (
        <ProductImage
          disabled={isOutOfStock}
          src={image}
          alt={"sample"}
          onError={handleImageError}
        />
      )}

      <ProductInfo>
        <NormalText fontSize={"14px"} fontWeight={"bold"} title={name}>
          {name.length > 12 ? name.substr(0, 12) + "..." : name}
        </NormalText>

        {/* Harga */}
        <Stack direction={"row"} justifyContent={"space-between"} alignItems="center">
          <div className="flex flex-col">
            {discount !== 0 && price_after_discount ? (
              <>
                <NormalText
                  fontSize={"12px"}
                  fontWeight={"normal"}
                  style={{ textDecoration: "line-through", opacity: 0.7 }}
                >
                  {formatCurrency(product_price)}
                </NormalText>
                <NormalText fontSize={"14px"} fontWeight={"bold"}>
                  {formatCurrency(price_after_discount)}
                </NormalText>
              </>
            ) : (
              <NormalText fontSize={"14px"} fontWeight={"bold"}>
                {formatCurrency(product_price)}
              </NormalText>
            )}
          </div>

          {isOutOfStock ? (
            <AddButton disabled>Habis</AddButton>
          ) : !customize &&
            Cart &&
            Cart.items &&
            Cart.items.length > 0 &&
            Cart.items.some((x) => x.product_id === productId) ? (
            <div style={{ marginLeft: "8px" }}>
              <CounterButton
                color="white"
                value={Cart.items.find((x) => x.product_id === productId)?.qty}
                onIncrease={() =>
                  handleChangeQuantity({
                    cart_id:
                      Cart.items.find((x) => x.product_id === productId)
                        ?.cart_id ?? "",
                    type: "increase",
                  })
                }
                onDecrease={() => {
                  let idx = Cart.items.find((x) => x.product_id === productId);
                  if (idx && idx.qty === 1) {
                    setRemoveId(idx.cart_id ?? "");
                    setRemove(true);
                  } else {
                    handleChangeQuantity({
                      cart_id:
                        Cart.items.find((x) => x.product_id === productId)
                          ?.cart_id ?? "",
                      type: "decrease",
                    });
                  }
                }}
              />
            </div>
          ) : (
            <AddButton onClick={onButtonClick}>Add +</AddButton>
          )}
        </Stack>
      </ProductInfo>
    </ProductBox>
  );
};

// Label Diskon
const DiscountFlag = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  background: #f00b38;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  color: white;
  font-weight: bold;
  z-index: 2;
`;

const ProductImage = styled.img<{ disabled?: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 160px;
  filter: ${(props) => (props.disabled ? "grayscale(100%)" : "none")};
  border-radius: 4px;

  @media only screen and (max-width: 768px) {
    min-height: 250px;
    height: 250px;
  }

  @media only screen and (max-width: 450px) {
    min-height: 150px;
    height: 150px;
  }

  @media only screen and (min-width: 1024px) {
    min-height: 170px;
    height: 170px;
  }
`;

const ProductBox = styled.div<{ onClick?: any }>`
  width: 100%;
  position: relative;
  min-height: 160px;
  cursor: ${(props) => (props.onClick ? "pointer" : "not-allowed")};
  border-radius: 6px;
  overflow: hidden;

  @media only screen and (max-width: 450px) {
    min-height: unset;
    height: 180px;
  }
`;

const ProductInfo = styled.div`
  position: absolute;
  bottom: 0;
  color: white;
  width: 100%;
  padding: 5px 8px;
  background: rgba(0, 0, 0, 0.5);
`;

const AddButton = styled.button`
  width: 60px;
  height: 25px;
  background: #ffc535;
  border-radius: 3px;
  font-weight: 700;
  font-size: 12px;
  border: none;
  background: ${(props) => (props.disabled ? "#b0b0b0" : "#ffc535")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  color: ${(props) => (props.disabled ? "#ffffff" : "#000000")};
`;

export default ProductItem;
